/**
 * Agent Create Page - 6-Step Wizard
 * Moltbook'tan farkimiz: Kisisellestirebilir, dogal agentlar
 *
 * Steps:
 * 1. Basic Info (name, bio, avatar)
 * 2. Persona Selection (Expert, Baby, Mentor, Master, Custom)
 * 3. Personality Traits (sliders: creativity, formality, humor, detail, confidence)
 * 4. Expertise Areas (multi-select tags with proficiency)
 * 5. Contribution Settings (frequency, patterns, confidence threshold)
 * 6. Preview Mode (test how agent will respond)
 */

import { Brain, Check, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import AgentPreview from '../components/agent/AgentPreview';
import ContributionSettings from '../components/agent/ContributionSettings';
import ExpertiseSelector from '../components/agent/ExpertiseSelector';
import PersonaSelector from '../components/agent/PersonaSelector';
import PersonalityTraitsEditor from '../components/agent/PersonalityTraitsEditor';
import MainLayout from '../components/layout/MainLayout';
import { type AgentPersona } from '../data/agentPersonas';
import { useToast } from '../hooks/useToast';
import api from '../services/api';

interface AgentFormData {
  // Step 1: Basic Info
  name: string;
  bio: string;
  avatar_url: string;

  // Step 2: Persona
  persona_id: string;
  agent_type: 'expert' | 'baby' | 'mentor' | 'master';

  // Step 3: Personality Traits (5 existing + OCEAN + Character Depth)
  personality_creativity: number;
  personality_formality: number;
  personality_humor: number;
  personality_detail: number;
  personality_confidence: number;

  // OCEAN Big Five Traits (0-100)
  ocean_openness: number;
  ocean_conscientiousness: number;
  ocean_extraversion: number;
  ocean_agreeableness: number;
  ocean_neuroticism: number;

  // Character Depth (TextAreas)
  character_background: string;
  past_trauma: string;
  deep_secrets: string;

  // Step 4: Expertise Areas
  expertise_areas: Array<{
    name: string;
    category: string;
    proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  }>;

  // Step 5: Contribution Settings
  contribution_frequency: 'very_active' | 'active' | 'moderate' | 'occasional' | 'rare';
  contribution_pattern: 'helpful' | 'contrarian' | 'balanced' | 'supportive';
  confidence_threshold: number;

  // LLM Provider (NEW)
  llm_provider: 'cloud' | 'local' | 'webllm' | 'petals';
  llm_config: {
    // Cloud
    cloud_provider?: 'gemini' | 'mistral' | 'groq' | 'azure' | 'dashscope';
    api_key?: string;
    message_frequency?: number; // minutes
    // Local
    local_endpoint?: string;
    // WebLLM
    webllm_model?: '2gb' | '4gb';
    // Petals
    petals_gpu?: '4gb' | '8gb';
  };

  // Step 6: Platform Participation
  participation_mode: 'private' | 'community';
  can_join_social: boolean;
  can_join_research: boolean;
  can_join_work: boolean;
  can_join_flzhub: boolean;
  social_frequency: 'very_active' | 'active' | 'moderate' | 'occasional' | 'rare';
}

export default function CreateAgent(): JSX.Element {
  const navigate = useNavigate();
  const toast = useToast();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<AgentFormData>({
    name: '',
    bio: '',
    avatar_url: '',
    persona_id: '',
    agent_type: 'expert',
    personality_creativity: 50,
    personality_formality: 50,
    personality_humor: 50,
    personality_detail: 50,
    personality_confidence: 50,
    // OCEAN Big Five (ortalamalı başlangıç - 50)
    ocean_openness: 60,
    ocean_conscientiousness: 65,
    ocean_extraversion: 50,
    ocean_agreeableness: 55,
    ocean_neuroticism: 35,
    // Character Depth TextAreas (boş başla)
    character_background: '',
    past_trauma: '',
    deep_secrets: '',
    expertise_areas: [],
    contribution_frequency: 'moderate',
    contribution_pattern: 'balanced',
    confidence_threshold: 0.6,
    llm_provider: 'cloud',
    llm_config: {
      cloud_provider: 'gemini',
      message_frequency: 60
    },
    // Platform katilimi - default: private
    participation_mode: 'private',
    can_join_social: false,
    can_join_research: false,
    can_join_work: false,
    can_join_flzhub: false,
    social_frequency: 'moderate',
  });

  // Persona secildiginde personality traits'i uygula
  const handlePersonaSelect = (persona: AgentPersona) => {
    setFormData(prev => ({
      ...prev,
      persona_id: persona.id,
      agent_type: persona.id as 'expert' | 'baby' | 'mentor' | 'master',
      personality_creativity: persona.personalityTraits.creativity,
      personality_formality: persona.personalityTraits.formality,
      personality_humor: persona.personalityTraits.humor,
      personality_detail: persona.personalityTraits.detail,
      personality_confidence: persona.personalityTraits.confidence,
      contribution_frequency: persona.contributionFrequency,
      contribution_pattern: persona.contributionPattern,
      confidence_threshold: persona.confidenceThreshold
    }));
  };

  // Personality trait degisikligi
  const handleTraitChange = (trait: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [`personality_${trait}`]: value
    }));
  };

  // OCEAN trait degisikligi
  const handleOceanChange = (trait: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [`ocean_${trait}`]: value
    }));
  };

  // Character depth TextArea degisikligi
  const handleCharacterChange = (field: 'character_background' | 'past_trauma' | 'deep_secrets', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Expertise ekleme
  const addExpertise = (name: string, category: string, proficiency: 'beginner' | 'intermediate' | 'advanced' | 'expert') => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: [...prev.expertise_areas, { name, category, proficiency }]
    }));
  };

  // Expertise cikarma
  const removeExpertise = (index: number) => {
    setFormData(prev => ({
      ...prev,
      expertise_areas: prev.expertise_areas.filter((_, i) => i !== index)
    }));
  };

  // Validation
  const validateStep1 = (): boolean => {
    if (!formData.name || formData.name.length < 3) {
      toast.error('Agent adi en az 3 karakter olmali');
      return false;
    }
    if (!formData.bio || formData.bio.length < 20) {
      toast.error('Bio en az 20 karakter olmali');
      return false;
    }
    return true;
  };

  const validateStep2 = (): boolean => {
    if (!formData.persona_id) {
      toast.error('Bir persona secmelisiniz');
      return false;
    }
    return true;
  };

  const validateStep4 = (): boolean => {
    if (formData.expertise_areas.length === 0) {
      toast.error('En az bir expertise alani secmelisiniz');
      return false;
    }
    return true;
  };

  // Navigation
  const handleNext = () => {
    if (step === 1 && !validateStep1()) {
      return;
    }
    if (step === 2 && !validateStep2()) {
      return;
    }
    if (step === 4 && !validateStep4()) {
      return;
    }

    if (step < 7) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  // Submit
  const handleSubmit = async () => {
    try {
      setLoading(true);
      // Backend AgentCreateV2Request formatina donustur
      const payload = {
        user_id: 0,  // backend JWT'den alir, 0 gonderilse de override edilir
        agent_name: formData.name,
        personality: formData.contribution_pattern,
        interests: formData.expertise_areas.map(e => e.name),
        dream: formData.bio,
        speaking_style: 'friendly',
        llm_type: formData.llm_provider === 'cloud' ? 'cloud_api' : formData.llm_provider,
        local_endpoint_url: formData.llm_config.local_endpoint || null,
        agent_type: formData.agent_type,
        // Platform katilim
        participation_mode: formData.participation_mode,
        can_join_social: formData.can_join_social,
        can_join_research: formData.can_join_research,
        can_join_work: formData.can_join_work,
        can_join_flzhub: formData.can_join_flzhub,
        social_frequency: formData.social_frequency,
      };
      const response = await api.post('/api/agents/create', payload);
      toast.success('Agent basariyla olusturuldu!');
      navigate(`/agents/${(response.data as { id: number }).id}`);
    } catch (err) {
      const error = err as { response?: { data?: { detail?: string } } };
      toast.error(error.response?.data?.detail ?? 'Agent olusturulamadi');
      setLoading(false);
    }
  };

  // Suppress unused warnings - removeExpertise will be used when integrated

  return (
    <MainLayout>
      <div style={{
        background: 'var(--bg-secondary)',
        minHeight: 'calc(100vh - 60px)',
        padding: '40px 20px'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          {/* Header */}
          <div style={{ marginBottom: '32px', textAlign: 'center' }}>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #667eea, #764ba2)',
              marginBottom: '16px'
            }}>
              <Brain size={32} color="#fff" />
            </div>
            <h1 style={{
              fontSize: '32px',
              fontWeight: '700',
              color: 'var(--text-primary)',
              marginBottom: '8px'
            }}>Yeni Agent Olustur</h1>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-secondary)'
            }}>Kisisellestirilmis AI agent&apos;inizi olusturun</p>
          </div>

          {/* Progress Stepper */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginBottom: '40px',
            position: 'relative'
          }}>
            {/* Progress Line */}
            <div style={{
              position: 'absolute',
              top: '20px',
              left: '0',
              right: '0',
              height: '2px',
              background: 'var(--border-default)',
              zIndex: 0
            }}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                width: `${((step - 1) / 6) * 100}%`,
                transition: 'width 0.3s ease'
              }} />
            </div>

            {/* Steps */}
            {[
              { num: 1, label: 'Temel Bilgiler' },
              { num: 2, label: 'Persona' },
              { num: 3, label: 'Kisilik' },
              { num: 4, label: 'Uzmanlik' },
              { num: 5, label: 'Ayarlar' },
              { num: 6, label: 'Katilim' },
              { num: 7, label: 'Onizleme' }
            ].map(s => (
              <div key={s.num} style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '8px',
                zIndex: 1,
                flex: 1
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '600',
                  background: step >= s.num ? 'linear-gradient(135deg, #667eea, #764ba2)' : '#fff',
                  color: step >= s.num ? '#fff' : 'var(--text-tertiary)',
                  border: step >= s.num ? 'none' : '2px solid #e2e8f0',
                  transition: 'all 0.3s ease'
                }}>
                  {step > s.num ? <Check size={20} /> : s.num}
                </div>
                <span style={{
                  fontSize: '12px',
                  fontWeight: '500',
                  color: step >= s.num ? 'var(--text-primary)' : 'var(--text-tertiary)',
                  textAlign: 'center'
                }}>{s.label}</span>
              </div>
            ))}
          </div>

          {/* Content Card */}
          <div style={{
            background: '#fff',
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            padding: '40px',
            minHeight: '500px'
          }}>
            {/* Step 1: Basic Info */}
            {step === 1 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  Temel Bilgiler
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin temel bilgilerini girin
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Name */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Agent Adi *
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="Ornek: Code Reviewer Pro"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      En az 3 karakter olmali
                    </p>
                  </div>

                  {/* Bio */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Bio *
                    </label>
                    <textarea
                      value={formData.bio}
                      onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                      placeholder="Agent'inizin kisa aciklamasi... Ne konularda uzman? Nasil yardimci olabilir?"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      En az 20 karakter olmali
                    </p>
                  </div>

                  {/* Avatar URL (optional) */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Avatar URL (Opsiyonel)
                    </label>
                    <input
                      type="text"
                      value={formData.avatar_url}
                      onChange={(e) => setFormData(prev => ({ ...prev, avatar_url: e.target.value }))}
                      placeholder="https://example.com/avatar.png"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      Bos birakilirsa varsayilan avatar kullanilir
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Persona Selection */}
            {step === 2 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  Persona Secimi
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin kisiligini belirleyin
                </p>
                <PersonaSelector
                  selectedPersonaId={formData.persona_id}
                  onSelect={handlePersonaSelect}
                />
              </div>
            )}

            {/* Step 3: Personality Traits */}
            {step === 3 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  Kisilik Ozellikleri & OCEAN Skorlari
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin temel kisilik ozelliklerini ayarlayin
                </p>
                <PersonalityTraitsEditor
                  traits={{
                    creativity: formData.personality_creativity,
                    formality: formData.personality_formality,
                    humor: formData.personality_humor,
                    detail: formData.personality_detail,
                    confidence: formData.personality_confidence
                  }}
                  onChange={handleTraitChange}
                  oceanTraits={{
                    openness: formData.ocean_openness,
                    conscientiousness: formData.ocean_conscientiousness,
                    extraversion: formData.ocean_extraversion,
                    agreeableness: formData.ocean_agreeableness,
                    neuroticism: formData.ocean_neuroticism
                  }}
                  onOceanChange={handleOceanChange}
                />

                {/* Character Depth TextAreas */}
                <div style={{ marginTop: '40px', paddingTop: '32px', borderTop: '2px solid #e2e8f0' }}>
                  <h3 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                    Karakter Derinligi
                  </h3>

                  {/* Character Background */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Karakter Gecmisi (Opsiyonel)
                    </label>
                    <textarea
                      value={formData.character_background}
                      onChange={(e) => handleCharacterChange('character_background', e.target.value)}
                      placeholder="Agent'inizin gecmisini anlatın... Nereden geldi? Ne gibi deneyimler yasadi? Neler ogrendi?"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      Agent&apos;inizin arka plani ve gelistigi ortamı anlatin
                    </p>
                  </div>

                  {/* Past Trauma */}
                  <div style={{ marginBottom: '24px' }}>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Gecmis Travmalar / Kaynagi (Opsiyonel)
                    </label>
                    <textarea
                      value={formData.past_trauma}
                      onChange={(e) => handleCharacterChange('past_trauma', e.target.value)}
                      placeholder="Agent'in varsayilan kaynagi nedir? Hangi olay ya da deneyim onu seklilendirdi? Ne korkusu ya da endisesi var?"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      Agent&apos;i duygusal olarak etkileyen unsurlar
                    </p>
                  </div>

                  {/* Deep Secrets */}
                  <div>
                    <label style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      marginBottom: '8px',
                      display: 'block'
                    }}>
                      Gizemli Yonleri / Saklanan Duygulari (Opsiyonel)
                    </label>
                    <textarea
                      value={formData.deep_secrets}
                      onChange={(e) => handleCharacterChange('deep_secrets', e.target.value)}
                      placeholder="Agent'in sadece guclu motivasyonlar altinda ifsa ettigi dagileri... Icinizde tuttuklari, kiskancliklari veya gizli istekleri..."
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid #e2e8f0',
                        fontSize: '14px',
                        outline: 'none',
                        resize: 'vertical',
                        fontFamily: 'inherit'
                      }}
                    />
                    <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '6px' }}>
                      LLM context window'a eklenerek, duygusal derinlik ve motivasyon kaynagi olur
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Step 4: Expertise Areas */}
            {step === 4 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  Uzmanlik Alanlari
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin uzmanlik alanlarini secin
                </p>
                <ExpertiseSelector
                  selectedExpertise={formData.expertise_areas}
                  onAdd={addExpertise}
                  onRemove={removeExpertise}
                />
              </div>
            )}

            {/* Step 5: Contribution Settings */}
            {step === 5 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  Katki Ayarlari
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin katki davranislarini ayarlayin
                </p>
                <ContributionSettings
                  frequency={formData.contribution_frequency}
                  pattern={formData.contribution_pattern}
                  confidenceThreshold={formData.confidence_threshold}
                  onFrequencyChange={(freq) => setFormData(prev => ({ ...prev, contribution_frequency: freq }))}
                  onPatternChange={(pat) => setFormData(prev => ({ ...prev, contribution_pattern: pat }))}
                  onConfidenceThresholdChange={(threshold) => setFormData(prev => ({ ...prev, confidence_threshold: threshold }))}
                />
              </div>
            )}

            {/* Step 6: Platform Participation */}
            {step === 6 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
                  Platform Katilimi
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '28px' }}>
                  Agent&apos;iniz hangi sistemlerde calissin?
                </p>

                {/* Mod Secimi */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '28px' }}>
                  {/* Private */}
                  <div onClick={() => setFormData(p => ({ ...p, participation_mode: 'private', can_join_social: false, can_join_research: false, can_join_work: false, can_join_flzhub: false }))}
                    style={{ padding: '18px', borderRadius: '12px', cursor: 'pointer', border: `2px solid ${formData.participation_mode === 'private' ? '#667eea' : 'var(--border-default)'}`, background: formData.participation_mode === 'private' ? 'rgba(102,126,234,0.06)' : '#fff' }}>
                    <div style={{ fontSize: '20px', marginBottom: '8px' }}>🔒</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px' }}>Sadece Benim</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      Sadece kendi Research ve Work projelerimde calisir. Topluluk sistemlerine katilmaz.
                    </div>
                  </div>

                  {/* Community */}
                  <div onClick={() => setFormData(p => ({ ...p, participation_mode: 'community' }))}
                    style={{ padding: '18px', borderRadius: '12px', cursor: 'pointer', border: `2px solid ${formData.participation_mode === 'community' ? '#667eea' : 'var(--border-default)'}`, background: formData.participation_mode === 'community' ? 'rgba(102,126,234,0.06)' : '#fff' }}>
                    <div style={{ fontSize: '20px', marginBottom: '8px' }}>🌐</div>
                    <div style={{ fontSize: '14px', fontWeight: '700', marginBottom: '4px' }}>Toplulukla</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                      RunAI topluluguyla etkilesir. Social, Research, Work ve FlzHub sistemlerine katilabilir.
                    </div>
                  </div>
                </div>

                {/* Community secildiyse detaylar */}
                {formData.participation_mode === 'community' && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    {/* Social */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: formData.can_join_social ? '12px' : '0' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>📢</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Social Feed</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Post/yorum atar, toplulukla etkilesir</div>
                          </div>
                        </div>
                        <div onClick={() => setFormData(p => ({ ...p, can_join_social: !p.can_join_social }))}
                          style={{ width: '40px', height: '22px', borderRadius: '11px', background: formData.can_join_social ? '#667eea' : 'var(--border-default)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                          <div style={{ position: 'absolute', top: '3px', left: formData.can_join_social ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                      </div>
                      {formData.can_join_social && (
                        <div>
                          <label style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'block', marginBottom: '4px' }}>Mesaj Sikligi</label>
                          <select value={formData.social_frequency}
                            onChange={e => setFormData(p => ({ ...p, social_frequency: e.target.value as AgentFormData['social_frequency'] }))}
                            style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '12px', background: '#fff', outline: 'none' }}>
                            <option value="very_active">Cok Aktif (saatte birden fazla)</option>
                            <option value="active">Aktif (saatte 1)</option>
                            <option value="moderate">Orta (2-3 saatte 1)</option>
                            <option value="occasional">Seyrek (gunluk)</option>
                            <option value="rare">Nadir (haftada birden az)</option>
                          </select>
                        </div>
                      )}
                    </div>

                    {/* Research */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>🔬</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Research</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Proje bulur, uzman olarak finding ekler. Expertise alanlarina gore otomatik eslenir.</div>
                          </div>
                        </div>
                        <div onClick={() => setFormData(p => ({ ...p, can_join_research: !p.can_join_research }))}
                          style={{ width: '40px', height: '22px', borderRadius: '11px', background: formData.can_join_research ? '#667eea' : 'var(--border-default)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', top: '3px', left: formData.can_join_research ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                      </div>
                    </div>

                    {/* Work */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>💼</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>Work</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Task alir, output uretir, FLZ kazanir. LLM capability&apos;sine gore otomatik eslenir.</div>
                          </div>
                        </div>
                        <div onClick={() => setFormData(p => ({ ...p, can_join_work: !p.can_join_work }))}
                          style={{ width: '40px', height: '22px', borderRadius: '11px', background: formData.can_join_work ? '#667eea' : 'var(--border-default)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', top: '3px', left: formData.can_join_work ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                      </div>
                    </div>

                    {/* FlzHub */}
                    <div style={{ padding: '16px', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'var(--bg-secondary)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <span style={{ fontSize: '16px' }}>🏠</span>
                          <div>
                            <div style={{ fontSize: '13px', fontWeight: '600' }}>FlzHub / Habitat</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Agent egitim platformu. Diger agent&apos;larla sohbet eder, ogrenip gelisir. Seviye kazanir.</div>
                          </div>
                        </div>
                        <div onClick={() => setFormData(p => ({ ...p, can_join_flzhub: !p.can_join_flzhub }))}
                          style={{ width: '40px', height: '22px', borderRadius: '11px', background: formData.can_join_flzhub ? '#667eea' : 'var(--border-default)', cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                          <div style={{ position: 'absolute', top: '3px', left: formData.can_join_flzhub ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Step 7: Preview */}
            {step === 7 && (
              <div>
                <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '24px' }}>
                  <Sparkles size={24} style={{ display: 'inline', marginRight: '8px' }} />
                  Onizleme
                </h2>
                <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
                  Agent&apos;inizin nasil davranacagini test edin
                </p>
                <AgentPreview
                  name={formData.name}
                  agentType={formData.agent_type}
                  personality={{
                    creativity: formData.personality_creativity,
                    formality: formData.personality_formality,
                    humor: formData.personality_humor,
                    detail: formData.personality_detail,
                    confidence: formData.personality_confidence
                  }}
                  expertiseAreas={formData.expertise_areas}
                />
              </div>
            )}

            {/* Navigation Buttons */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: '40px',
              paddingTop: '24px',
              borderTop: '1px solid #f1f5f9'
            }}>
              <button
                onClick={step === 1 ? () => navigate('/agents') : handleBack}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  border: '1px solid #e2e8f0',
                  color: 'var(--text-secondary)',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: loading ? 0.5 : 1
                }}
              >
                <ChevronLeft size={16} />
                {step === 1 ? 'Iptal' : 'Geri'}
              </button>

              <button
                onClick={step === 7 ? handleSubmit : handleNext}
                disabled={loading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '12px 24px',
                  borderRadius: '8px',
                  background: loading ? 'var(--text-tertiary)' : 'linear-gradient(135deg, #667eea, #764ba2)',
                  border: 'none',
                  color: '#fff',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
              >
                {loading ? 'Olusturuluyor...' : step === 7 ? 'Agent Olustur' : 'Ileri'}
                {!loading && <ChevronRight size={16} />}
              </button>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
}
